import { useEffect, useRef } from 'react'
import { useAtomValue } from 'jotai'
import { useRouterState } from '@tanstack/react-router'
import { loggedInEmailAtom } from '@/state/authentication/login'
import { registrationEmailAtom } from '@/state/global'
import { identifyClaritySession } from '@/services/telemetry/clarity'

// Ties the Clarity session to the signed-in user, re-identifying on every
// route change. Must render under RouterProvider (uses useRouterState).
export function ClarityIdentityTracker() {
  const loggedInEmail = useAtomValue(loggedInEmailAtom)
  const registrationEmail = useAtomValue(registrationEmailAtom)
  const pathname = useRouterState({ select: (s) => s.location.pathname })

  const hasIdentifiedRealUser = useRef(false)
  const anonymousIdRef = useRef<string | null>(null)

  useEffect(() => {
    // loggedInEmailAtom persists in localStorage, so only trust it if this
    // tab actually signed in — avoids identifying a new visitor as whoever
    // last used this browser.
    const isSignedInThisVisit = sessionStorage.getItem('isSignedIn') === 'true'
    const email = isSignedInThisVisit
      ? loggedInEmail || registrationEmail || null
      : null

    if (email) {
      hasIdentifiedRealUser.current = true
      identifyClaritySession(email, pathname)
      return
    }

    // Logged out: reset to a one-time anonymous id instead of leaving the
    // old identity active for the rest of the tab's life.
    if (hasIdentifiedRealUser.current) {
      anonymousIdRef.current ??= crypto.randomUUID()
      identifyClaritySession(anonymousIdRef.current, pathname)
      hasIdentifiedRealUser.current = false
    }
  }, [loggedInEmail, registrationEmail, pathname])

  return null
}
